import React, { useMemo } from 'react';
import { Form } from '../../../../../../v2BaseComponents/Form';
import { useIsDisabledByParent } from '../../../../../../v2BaseComponents/IsDisabledProvider';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
    SideDrawerDirtyFormExitValidationTracker,
} from '../../../../../../v2BaseComponents/Form/SideDrawerFormContainer';
import useOrganizationClientInvitations from '../../../../../../../hooks/api/useOrganizationClientInvitations';
import useOrganizationInvitationDefinitions from '../../../../../../../hooks/api/useOrganizationInvitationDefinitions';
import OrganizationClientInvitationFields, {
    clientInvitationToFormValue,
} from '../../../../../../shared/OrganizationClientInvitations/OrganizationClientInvitationFields';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import MaybePublicOrgMissingInfoAlert from '../../../../../../shared/MaybePublicOrgMissingInfoAlert';
import { getIntl } from '../../../../../../../utils/intl';
import ClientInvitationProvider, { ClientInvitationValue } from '../../../../../../shared/ClientInvitationProvider';

const intl = getIntl('profiles.editClientInvitationSideDrawerContent', {
    update: 'Update',
});

export type EditClientInvitationSideDrawerContentOwnProps = {
    onClose: (force: boolean) => void;
    clientRecordId: string;
};

export type EditClientInvitationSideDrawerContentProps = EditClientInvitationSideDrawerContentOwnProps;

const Content: React.FC<ClientInvitationValue & EditClientInvitationSideDrawerContentProps> = ({
    clientInvitation,
    clientRecord,
    onClose,
}) => {
    const initialValues = useMemo(
        () => clientInvitationToFormValue(clientInvitation, !!clientRecord.provisionedMedicalProfileId),
        [clientInvitation, clientRecord],
    );
    const { updateClientInvitation, isUpdatingClientInvitation } = useOrganizationClientInvitations({
        onUpdateInvitation: () => onClose(true),
    });
    const { isLoadingInvitationDefinitions, invitationDefinitions } = useOrganizationInvitationDefinitions({});
    const isDisabled = useIsDisabledByParent(isUpdatingClientInvitation);

    return (
        <AnimateLoadedContent isLoading={isUpdatingClientInvitation || isLoadingInvitationDefinitions}>
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <Form
                        fillContainer
                        initialValues={initialValues}
                        onSubmit={(values) =>
                            updateClientInvitation({
                                invitationId: clientInvitation.organizationClientInvitationId,
                                update: {
                                    ...values,
                                    expirationDateTime: values.expirationDateTime
                                        ? new Date(values.expirationDateTime).toISOString()
                                        : undefined,
                                },
                            })
                        }
                        isDisabled={isDisabled}
                        forceStackFields
                    >
                        {({ isSubmitting, handleSubmit, values }) => {
                            return (
                                <SideDrawerFormContainer
                                    isDisabled={isDisabled || isSubmitting}
                                    onSubmit={handleSubmit}
                                    onCancel={() => onClose(true)}
                                    saveText={intl('update')}
                                    saveIcon="CheckCircle"
                                >
                                    <SideDrawerDirtyFormExitValidationTracker />
                                    <MaybePublicOrgMissingInfoAlert />
                                    <OrganizationClientInvitationFields
                                        invitationDefinitions={invitationDefinitions}
                                        value={values}
                                        disableProvisionProfileReason={'editing-invitation'}
                                    />
                                </SideDrawerFormContainer>
                            );
                        }}
                    </Form>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

const EditClientInvitationSideDrawerContent: React.FC<EditClientInvitationSideDrawerContentProps> = ({
    onClose,
    clientRecordId,
}) => {
    return (
        <ClientInvitationProvider clientRecordId={clientRecordId}>
            {({ clientInvitation, clientRecord }) => (
                <Content
                    clientInvitation={clientInvitation}
                    clientRecord={clientRecord}
                    onClose={onClose}
                    clientRecordId={clientRecordId}
                />
            )}
        </ClientInvitationProvider>
    );
};

export default React.memo(EditClientInvitationSideDrawerContent);
